import type { ReactNode } from "react";
import { Tabs as TabsRadix } from "radix-ui";
import styles from "./Tabs.css";

type Tab = {
  value: string;
  label: string;
  content: ReactNode;
};

type TabsProps = {
  tabs: Tab[];
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
};

type TabsRootProps = {
  children: ReactNode;
  /** The selected tab, for a caller that owns the state. */
  value?: string;
  onValueChange?: (value: string) => void;
  /** The tab selected initially, when the caller does not own the state. */
  defaultValue?: string;
};

type TabsListProps = {
  tabs: Pick<Tab, "value" | "label">[];
  ariaLabel: string;
};

type TabsContentProps = {
  value: string;
  children: ReactNode;
};

/**
 * The tab state's provider and nothing else: it renders no box of its own
 * (`display: contents`), so it can wrap a whole page layout — a PageShell whose
 * header holds the `List` and whose body holds the `Content`s — without
 * standing between that layout and its parent. The parts talk through context,
 * so they only have to be descendants, not siblings.
 */
const TabsRoot = ({
  children,
  value,
  onValueChange,
  defaultValue,
}: TabsRootProps) => (
  <TabsRadix.Root
    className={styles.root}
    value={value}
    onValueChange={onValueChange}
    defaultValue={defaultValue}
  >
    {children}
  </TabsRadix.Root>
);

/** The row of triggers. Sizes to its content, so it can sit in a header. */
const TabsList = ({ tabs, ariaLabel }: TabsListProps) => (
  <TabsRadix.List className={styles.list} aria-label={ariaLabel}>
    {tabs.map((tab) => (
      <TabsRadix.Trigger
        key={tab.value}
        className={styles.trigger}
        value={tab.value}
      >
        {tab.label}
      </TabsRadix.Trigger>
    ))}
  </TabsRadix.List>
);

/** One tab's panel. Fills the column it sits in; only the selected one renders. */
const TabsContent = ({ value, children }: TabsContentProps) => (
  <TabsRadix.Content className={styles.content} value={value}>
    {children}
  </TabsRadix.Content>
);

/**
 * Tabs with the triggers stacked directly above their panels. A page that has
 * to split the two — triggers in a header, panels in a body — composes
 * `Tabs.Root`, `Tabs.List` and `Tabs.Content` itself instead.
 */
const Tabs = ({ tabs, value, onValueChange, ariaLabel }: TabsProps) => (
  <TabsRadix.Root
    className={styles.stack}
    value={value}
    onValueChange={onValueChange}
  >
    <TabsList tabs={tabs} ariaLabel={ariaLabel} />
    {tabs.map((tab) => (
      <TabsContent key={tab.value} value={tab.value}>
        {tab.content}
      </TabsContent>
    ))}
  </TabsRadix.Root>
);

Tabs.Root = TabsRoot;
Tabs.List = TabsList;
Tabs.Content = TabsContent;

export default Tabs;
